<script setup>
import { i18n } from 'boot/i18n';
import { TotpMultiFactorGenerator } from 'firebase/auth';
import { useDialogPluginComponent } from 'quasar';
import {
  HrAdaptiveFormDialog, HrConfirmBtn, HrDismissBtn, HrTextField, notifyNegative,
} from 'src/design-system';
import { ref } from 'vue';

defineOptions({ name: 'VerifyAuthenticatorAppMfa' });

const props = defineProps({
  resolver: {
    type: Object,
    required: true,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const code = ref('');
const codeVerificationInProgress = ref(false);

async function onSubmitCode() {
  if (!code.value) {
    return;
  }

  codeVerificationInProgress.value = true;
  try {
    if (!props.resolver.hints || !props.resolver.hints[0]) {
      throw new Error(i18n.t('users.modals.verifyMFA.errorRetrievingUserCredentials'));
    }

    const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
      props.resolver.hints[0].uid,
      code.value.trim(),
    );
    const userCredential = await props.resolver.resolveSignIn(multiFactorAssertion);

    if (!userCredential) {
      throw new Error(i18n.t('users.modals.verifyMFA.errorRetrievingUserCredentials'));
    }

    onDialogOK(userCredential);
  } catch (error) {
    if (error.code === 'auth/requires-recent-login' || error.code === 'auth/totp-challenge-timeout') {
      notifyNegative(i18n.t('users.modals.verifyMFA.recentLoginRequiredError'));
      onDialogCancel();
      return;
    }

    if (error.code === 'auth/quota-exceeded') {
      notifyNegative(i18n.t('users.modals.verifyMFA.quotaExceededAuthenticatorAppError'));
      return;
    }

    if (error.code === 'auth/invalid-verification-code') {
      notifyNegative(i18n.t('users.modals.verifyMFA.invalidAuthenticatorAppCodeError'));
      return;
    }

    notifyNegative(error.message);
  } finally {
    codeVerificationInProgress.value = false;
  }
}
</script>

<template>
  <hr-adaptive-form-dialog
    ref="dialogRef"
    :title="i18n.t('users.modals.verifyMFA.title')"
    @hide="onDialogHide"
    @close="onDialogCancel"
    @submit="onSubmitCode"
  >
    <p>{{ i18n.t('users.modals.verifyMFA.textAuthenticatorAppCaption') }}</p>
    <hr-text-field
      v-model="code"
      :loading="codeVerificationInProgress"
      :disable="codeVerificationInProgress"
      mask="######"
      autofocus
      :label="i18n.t('users.modals.verifyMFA.inputAuthenticatorAppCodeLabel')"
      :rules="[(val) => !!val || i18n.t('users.modals.verifyMFA.emptyCodeError')]"
      lazy-rules="ondemand"
      autocomplete="one-time-code"
      data-e2e="input-mfa-code"
    />
    <template #actions="{ isMobile }">
      <hr-dismiss-btn
        v-if="!isMobile"
        :label="i18n.t('common.buttonCancel')"
        @click="onDialogCancel"
      />
      <hr-confirm-btn
        type="submit"
        :label="i18n.t('users.modals.verifyMFA.buttonVerify')"
        :loading="codeVerificationInProgress"
        :disable="codeVerificationInProgress"
      />
    </template>
  </hr-adaptive-form-dialog>
</template>
